import React, { Fragment, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useBeforeunload } from 'react-beforeunload';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import flatten from 'flat-util';
import { differenceBy, uniqBy } from 'lodash';

import { ALERT_CODES, DEFAULT_SAVE_STATUS } from 'src/components/JobChecklistForm/constants';
import { CHECKLIST_VERSION_BASE_QUERY } from 'src/graphql/checklist/query';
import { Clearfix } from 'src/components/Clearfix/Clearfix';
import { DELETE_ATTACHMENT_MUTATION } from 'src/graphql/attachment/mutation';
import { useMutation } from 'src/graphql';
import { CollapsibleBlock } from 'src/components/SurveyForm/constants';
import { DatePickerField } from 'src/components/DatePickerField/DatePickerField';
import { flatToTree, treeToFlat } from 'src/utils/form/section';
import { FormRow } from 'src/components/Form';
import { withJobContext } from 'src/utils/context/JobContext';
import {
  generateDefaultChecklistResults,
  getButtonTextOfFilterBy,
  getChecklistMergedResults,
  getChecklistOutcomes,
  getFactoryQuestion,
  getQuestionOutcomes,
  getUniqueAlerts,
  isNegativeOutcome,
  isPositiveOutcome,
  saveStatusReducer,
} from 'src/components/JobChecklistForm/utils';
import {
  getActiveReportReviewNote,
  getJobParamFromCompanies,
} from 'src/components/SurveyForm/utils';
import { JobChecklistFormPropTypes } from 'src/components/JobChecklistForm/propTypes';
import { JobChecklistFormSection } from 'src/components/JobChecklistForm/JobChecklistFormSection';
import { JobCustomPanelBlock } from 'src/components/JobCustomPanelBlock/JobCustomPanelBlock';
import { NonComplianceBlock } from 'src/components/JobChecklistForm/NonComplianceBlock';
import { notification } from 'src/components/Notification';
import { ReportReviewBlock } from 'src/components/ReportReviewBlock';
import { ResultHistoriesBlock } from 'src/components/ResultHistoriesBlock';
import { SurveyType } from 'src/utils/data/types';
import Attachments from 'src/components/Attachments';
import { UNDER_INTERNAL_SAQ, UNDER_REVIEW } from 'src/utils/data/job';
import Box from 'src/components/Box';
import client from 'src/utils/api/client';
import JobFormCountDown from 'src/components/JobFormCountDown';
import Loader from 'src/components/Loader';
import { getQuestionNumber } from 'src/components/ChecklistPreviewForm/utils';
import PopoverSelect from 'src/components/PopoverSelect';
import s from 'src/components/JobChecklistForm/JobChecklistForm.styles';
import TextField from 'src/components/TextField';

const JobChecklistFormComponent = ({
  jobContext,
  activeTab,
  checklistBase,
  checklistsCache,
  setChecklistVersions,
  getResultHistories,
  isUnderReviewing,
  job,
  onSave,
  readonly,
  refetchReportReview,
  refetchResultHistories,
  reportReviewData,
  reportReviewStatistics,
  resultHistories,
  results,
  setResults,
  storedResult,
}) => {
  const { t } = useTranslation();

  const {
    user: {
      isAuditor,
      isReportReviewer,
    },
    permissions: {
      isReportNotesAvailableToAuditor,
    },
  } = jobContext;

  const checklistVersionId = checklistBase.currentVersion.id;
  const [loading, setLoading] = useState(false);
  const [checklist, setChecklist] = useState(checklistsCache[checklistVersionId] || checklistBase);

  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT_MUTATION);

  const companies = job.jobCompanies.filter(c => !!c.company);
  const isJobStatusReviewOrSaq = job.status === UNDER_REVIEW || job.status === UNDER_INTERNAL_SAQ;
  const factory = getJobParamFromCompanies(companies, 'Factory');
  const program = getJobParamFromCompanies(companies, 'Program');
  const programId = program?.company?.id;
  const serviceId = checklistBase?.serviceId;

  const statistic = reportReviewStatistics.find(
    d => d.type === 'checklist' && +d.typeId === +checklistBase.id,
  );

  const [collapsedSections, setCollapsedSections] = useState([]);
  const [timer, setTimer] = useState([]);

  const [isFiltered, setIsFiltered] = useState(true);

  const toggleSection = id => {
    const newCollapsedSections = [...collapsedSections];
    if (collapsedSections.indexOf(id) === -1) {
      newCollapsedSections.push(id);
    } else {
      newCollapsedSections.splice(newCollapsedSections.indexOf(id), 1);
    }
    setCollapsedSections(newCollapsedSections);
  };

  const [collapsedBlocks, setCollapsedBlocks] = useState(Object.values(CollapsibleBlock));

  const toggleBlock = id => {
    const newCollapsedBlocks = [...collapsedBlocks];
    if (collapsedBlocks.indexOf(id) === -1) {
      newCollapsedBlocks.push(id);
    } else {
      newCollapsedBlocks.splice(newCollapsedBlocks.indexOf(id), 1);
    }
    setCollapsedBlocks(newCollapsedBlocks);
  };
  const isCollapsedBlock = id => collapsedBlocks.indexOf(id) !== -1;

  const [isCollapsed, setIsCollapsed] = useState(false);
  const collapseAll = () => setCollapsedSections(
    (checklist?.currentVersion?.checklistSections || []).map(v => v.position),
  );
  const expandAll = () => setCollapsedSections([]);
  

  const [selectedQuestion, setSelectedQuestion] = useState();

  const activeSection = selectedQuestion
    ? checklist?.currentVersion?.checklistSections.find(
      section => section.position === selectedQuestion.section,
    )
    : null;
  
  const activeQuestion = activeSection
    ? activeSection.questions.find(
      question => question.position === selectedQuestion.question,
    )
    : null;
  
  /* outcomes of all the questions in checklist */
  const questionsOutcomes = useMemo(() => {
    return uniqBy(treeToFlat(flatToTree(checklist?.currentVersion?.checklistSections || []))
      .map(({ questions }) => {
        return questions
          .map(({ resultFamily }) => resultFamily?.currentVersion?.resultFamilyOutcomes || null).flat();
      })
      .flat()
      .filter(v => !!v), 'resultId');
  }, [checklist?.currentVersion?.defaultResultFamily?.currentVersion?.resultFamilyOutcomes]);

  /* outcomes of the active question only */
  const activeQuestionOutcomes = useMemo(() => getQuestionOutcomes(
    activeQuestion
  ), [activeQuestion?.resultFamily?.currentVersion?.resultFamilyOutcomes]);

  /* outcomes from default result family of checklist */
  const checklistOutcomes = useMemo(() => getChecklistOutcomes(
    checklist
  ), [checklist?.currentVersion?.defaultResultFamily?.currentVersion?.resultFamilyOutcomes]);

  /* if question is overriding the default result family of checklist that will
  be given preference */
  const outcomes = useMemo(() => activeQuestionOutcomes || checklistOutcomes || [],
    [activeQuestionOutcomes, checklistOutcomes]
  );

  /* positive outcomes for active question that may come from question or
  default result family of the checklist */
  const activeQuestionOrChecklistPositiveOutcomes = useMemo(()=>{
    return outcomes?.filter(outcome => isPositiveOutcome(outcome)) || [];
  },[outcomes]);

  /* negative outcomes for active question that may come from question or
  default result family of the checklist */
  const activeQuestionOrChecklistNegativeOutcomes = useMemo(()=>{
    return outcomes?.filter(outcome => isNegativeOutcome(outcome)) || [];
  },[outcomes]);

  /* nor positive neither negative outcomes for active question that may come
  from question or default result family of the checklist */
  const activeQuestionOrChecklistOtherOutcomes = useMemo(() => {
    return differenceBy(outcomes, [
      ...activeQuestionOrChecklistPositiveOutcomes,
      ...activeQuestionOrChecklistNegativeOutcomes,
    ],
    'resultId');
  }, [activeQuestionOrChecklistPositiveOutcomes, activeQuestionOrChecklistNegativeOutcomes]);

  /* combined outcomes from all the questions' results and checklist default result family */
  const allOutcomes = useMemo(() => uniqBy(questionsOutcomes?.concat(checklistOutcomes || []) || [], 'resultId'),
    [questionsOutcomes, checklistOutcomes]
  );

  /* combined negative outcomes from all the questions' results and checklist default result family */
  const allNegativeOutcomes = useMemo( () =>{
    return allOutcomes?.filter(outcome => isNegativeOutcome(outcome)) || [];
  },[allOutcomes]);

  /* combined positive outcomes from all the questions' results and checklist default result family */
  const allPositiveOutcomes = useMemo( () =>{
    return allOutcomes?.filter(outcome => isPositiveOutcome(outcome)) || [];
  },[allOutcomes]);

  /* combined non-negative and non-positive outcomes from all the questions' results
  and checklist default result family */
  const allOtherOutcomes = useMemo( () =>{
    return differenceBy(allOutcomes, [...allNegativeOutcomes, ...allPositiveOutcomes], 'resultId');
  },[allOutcomes]);

  /**
   * ids array of three types of combined outcomes of all the questions' results
   * and checklist's default results
   */
  const negativeOutcomeResultIds = allNegativeOutcomes.map(({ resultId }) => resultId);
  const positiveOutcomeResultIds = allPositiveOutcomes.map(({ resultId }) => resultId);
  const allOtherOutcomeResultIds = allOtherOutcomes.map(({ resultId }) => resultId);

  const defaultChecklistResults = useMemo(() => generateDefaultChecklistResults(
    checklist,
    checklistOutcomes
  ), [checklist?.currentVersion?.checklistSections, checklistOutcomes]);

  const questionsWithMissingResults = useMemo(() => {
    return results
      .filter(result => result.isMissingResults && !result.resultId)
      .map(result => ({ checklistQuestionId: result.checklistQuestionId, checklistId: result.checklistId }));
  }, [results]);

  useEffect(() => {
    setResults(getChecklistMergedResults(
      defaultChecklistResults,
      (storedResult || {}).data || []
    ));
  }, [defaultChecklistResults.length]);

  const [saveStatus, dispatchSaveStatus] = useReducer(saveStatusReducer, DEFAULT_SAVE_STATUS);
  const { isFailed, lastSavedBlocks, retrying, savingBlocks } = saveStatus;

  useBeforeunload((event) => {
    if (isFailed) {
      event.preventDefault();
    }
  });

  useEffect(() => {
    if (!activeTab) {
      dispatchSaveStatus({ type: 'CLEAR_LAST_SAVED_BLOCK' });
    }
  }, [activeTab]);

  const setSaveError = (data, hasError) => {
    dispatchSaveStatus({ type: 'SET_SAVE_STATUS', hasError });
  };

  const isBlockSaveFailed = (block) => {
    return isFailed ? savingBlocks.indexOf(block) !== -1 : false;
  };

  const isBlockSaveSuccess = (block) => {
    return lastSavedBlocks.indexOf(block) !== -1;
  };
  let resultsChanged = [...results];
  const setResult = async (question, field, value) => {
    if (readonly) {
      notification.error(readonly);
      return;
    }
    const newResults = JSON.stringify(resultsChanged) !== JSON.stringify(results) ? [...resultsChanged] : [...results];
    const index = newResults.findIndex(v => v.checklistQuestionId === question.checklistQuestionId);

    if (index !== -1) {
     // newResults[index][field] = value;
     newResults[index] = {
      ...newResults[index],
      [field]: value,
    };
    } else {
      const indexInDefault = defaultChecklistResults.findIndex(v => v.checklistQuestionId === question.checklistQuestionId);
      if (indexInDefault !== -1) {
        newResults.push({
          ...defaultChecklistResults[indexInDefault],
          [field]: value,
        });
      }
    }

    resultsChanged = newResults;

    await setResults(newResults);
    await dispatchSaveStatus({ type: 'ADD_SAVING_BLOCK', block: field });

    await onSave(newResults[index], setSaveError);
    await dispatchSaveStatus({ type: 'CLEAR_LAST_SAVED_BLOCK' });
    await refetchResultHistories();
  };

  const getResult = question => results.find(
    v => v.checklistQuestionId === question?.checklistQuestionId,
  ) || {};

  const getResultFromNewResults = question => resultsChanged.find(
    v => v.checklistQuestionId === question?.checklistQuestionId,
  ) || {};

  const retryAutoSave = question => {
    const newResults = [...results];
    const index = newResults.findIndex(
      v => v.checklistQuestionId === question.checklistQuestionId,
    );

    if (timer[index]) {
      clearTimeout(timer[index]);
    }
    timer[index] = setTimeout(() => {
      onSave(newResults[index], setSaveError);
    }, 1500);

    setTimer(timer);
    dispatchSaveStatus({ type: 'RETRY_AUTO_SAVE' });
  };

  const setResultNonCompliance = (question, index, field, value, flag = false) => {
    const newNonCompliances = flag ? [...(getResultFromNewResults(question).nonCompliances || [])] : [...(getResult(question).nonCompliances || [])];
    // newNonCompliances[index][field] = value;
    newNonCompliances[index] = { ...newNonCompliances[index], [field]: value };
    setResult(question, 'nonCompliances', newNonCompliances)
      .finally(()=>{
        dispatchSaveStatus({ type: 'ADD_SAVING_BLOCK', block: `nonCompliances${index}` });
      });

    if (['legalRequirements', 'detailedFinding', 'recommendations'].indexOf(field) !== -1) {
      setResult(question, field, newNonCompliances.map(c => c[field]).join('\n\n'));
    }
  };

  const addResultNonCompliance = question => {
    const isNegativeOutcomeSelected = (activeQuestionOrChecklistNegativeOutcomes?.map(v => v.resultId) || [])
      .includes(getResult(question).resultId);
    const findingType = isNegativeOutcomeSelected ? 'new' : 'previous';

    const newNonCompliances = [...(getResult(question).nonCompliances || [])];
    newNonCompliances.push({
      id: null,
      lawId: null,
      findingType,
    });
    setResult(question, 'nonCompliances', newNonCompliances);
  };

  const removeResultNonCompliance = (question, index) => {
    const newNonCompliances = [...(getResult(question).nonCompliances || [])];
    newNonCompliances.splice(index, 1);
    setResult(question, 'nonCompliances', newNonCompliances);

    if (question?.checklistQuestion?.currentVersion?.isWildCardQuestion && (newNonCompliances.length === 0)) {
      setResult(question, 'resultId', activeQuestionOrChecklistPositiveOutcomes[0]?.resultId);
      setSelectedQuestion(null);
    }

    ['legalRequirements', 'detailedFinding', 'recommendations'].forEach ( field =>{
      setResult(question, field, newNonCompliances.map(c => c[field]).join('\n\n'));
    }
    );
  };

  const calcLegalRequirements = (question, nonCompliance) => {
    const nonComplianceLaws = flatten(question.checklistQuestion.currentVersion.nonCompliances
      .filter(v => v.id === nonCompliance.id)
      .map(v => v.laws), true);

    let countryLaws = [];
    if (factory) {
      const companyAddress = factory.company.currentVersion.companyAddresses[0];
      if (companyAddress && companyAddress.currentVersion.country) {
        countryLaws = companyAddress.currentVersion.country.laws;
      }
    }

    const allLawsOptions = [...nonComplianceLaws, ...countryLaws].map(v => ({
      articleSecNo: v.currentVersion.articleSecNo,
      label: v.currentVersion.legislationName,
      legalCitation: v.currentVersion.legalCitation,
      value: v.id,
    }));

    const item = allLawsOptions.find(
      v => v.value === (nonCompliance.lawId || nonCompliance.law),
    );

    if (!item) {
      return null;
    }

    let lawLabel = item.label;
    if (item.articleSecNo) {
      lawLabel += ` ${item.articleSecNo}`;
    }

    return `In accordance with ${lawLabel}, ${item.legalCitation}`;
  };

  const setResultNonComplianceByObject = (question, index, data) => {
    const newNonCompliances = [...(getResult(question).nonCompliances || [])];
    newNonCompliances[index] = {
      ...newNonCompliances[index],
      ...data,
    };
    setResult(question, 'nonCompliances', newNonCompliances);

    Object.keys(data).forEach(field => {
      if (
        ['legalRequirements', 'detailedFinding', 'recommendations'].indexOf(
          field,
        ) !== -1
      ) {
        setResult(
          question,
          field,
          newNonCompliances.map(c => c[field]).join('\n\n')
        );
      }
      if (['law', 'lawId'].indexOf(field) !== -1) {
        setTimeout(() => setResultNonCompliance(
          question,
          index,
          'legalRequirements',
          calcLegalRequirements(question, newNonCompliances[index]),
          true
        ), 500);
      }
    });

    dispatchSaveStatus({ type: 'ADD_SAVING_BLOCK', block: `nonCompliances${index}` });
  };

  const [ALL, NOTE_EXISTED, ACTION_NEEDED, NEGATIVE_OUTCOME, CORRECTED_FINDING] = [
    'All',
    'Note',
    'Action needed',
    'Negative outcome',
    'Corrected finding',
  ];
  const filterTags = [ALL, NOTE_EXISTED, ACTION_NEEDED, NEGATIVE_OUTCOME, CORRECTED_FINDING];

  const [currentFilter, setCurrentFilter] = useState(ALL);

  const getSectionQuestions = (section, questions) => {
    switch (currentFilter) {
      case NOTE_EXISTED:
        if (!statistic) {
          return [];
        }

        return questions.filter(q =>
          statistic.noteExistedQuestionIds.includes(q.id),
        );
      case ACTION_NEEDED:
        if (!statistic) {
          return [];
        }

        return questions.filter(q =>
          statistic.actionRequiredtypeQuestionIds.includes(q.id),
        );
      case NEGATIVE_OUTCOME:
        return questions.filter(
          q => negativeOutcomeResultIds.includes(getResult(q).resultId),
        );
      case CORRECTED_FINDING:
        return questions.filter(
          q => getResult(q).correctedDate,
        );
      default:
        return questions;
    }
  };

  const applyQuestionConditions = section => {
    return section.questions.filter(question => {
      if (!question.conditions || question.conditions.length === 0) {
        return true;
      }
      
      const conditionAction = question.conditions[0].action || '';
      const conditionParts = [];

      question.conditions.forEach(condition => {
        let parentQuestion ;
        // eslint-disable-next-line
        (checklist?.currentVersion?.checklistSections || []).some(sect =>{
          parentQuestion =sect.questions.find(q => q.checklistQuestionId === condition.checklistQuestionId)
          if(parentQuestion) return true;
      });
        const parentResult = getResult(parentQuestion).resultId;

        if (condition.logicalOperator === 'and') {
          conditionParts.push('&&');
        } else if (condition.logicalOperator === 'or') {
          conditionParts.push('||');
        }
        conditionParts.push(
          parentResult === condition.resultId ? 'true' : 'false',
        );
      });
      conditionParts.splice(0, 1);

      return (conditionAction === 'show' ? 
        eval(conditionParts.join(' ')) : // eslint-disable-line no-eval
        !eval(conditionParts.join(' '))); // eslint-disable-line no-eval
    });
  };

  const sectionRefs = [...Array(1000).keys()].map(() => useRef(null));

  let sectionOptionIndex = 0;
  const getNextSectionRef = () => {
    const ref = sectionRefs[sectionOptionIndex];
    sectionOptionIndex += 1;
    return ref;
  };

  const getSectionOptions = (sections, indexPath = []) => {
    const labelCustomNumber = (section) => [
      ...indexPath,
      section.checklistSectionIdAlias || `${section.position + 1}.`
    ].join('');

    return sections.map(v => {
      return [
        {
          label: `${labelCustomNumber(v)} ${v.name}`,
          level: indexPath.length,
          ref: getNextSectionRef(),
          value: v.originalIndex,
        },
        ...getSectionOptions(
          v.children,
          [...indexPath, v.checklistSectionIdAlias || `${v.position + 1}.`]
        ).flat(),
      ];
    });
  };

  const [quickJumpOpened, setQuickJumpOpened] = useState(false);
  const sectionsOptions = getSectionOptions(
    flatToTree(
      checklist?.currentVersion?.checklistSections || [],
      null,
      SurveyType.Checklist
    ),
  ).flat();
  
  const sectionsWrapper = useRef(null);

  const [displayFollowUp, setDisplayFollowUp] = useState(false);

  const nonCompliances = activeQuestion
    ? activeQuestion.checklistQuestion.currentVersion.nonCompliances
    : [];
  const nonCompliancesOptions = nonCompliances.map(v => ({
    value: v.id,
    label: v.description,
  }));

  const activeReportReviewNote = getActiveReportReviewNote(reportReviewData, activeQuestion);

  const [isCreateNote, setIsCreateNote] = useState(false);

  useLayoutEffect(() => {
    setIsCreateNote(false);
  }, [activeQuestion]);

  const addNote = () => {
    setIsCreateNote(true);
  };

  const [showResult, setShowResult] = useState(activeReportReviewNote);
  const [showNote, setShowNote] = useState(!activeReportReviewNote);
  const [showHistory, setShowHistory] = useState(false);

  const toggleSelectedQuestion = (section, question, questionNumber) => {
    if (
      selectedQuestion &&
      selectedQuestion.section === section &&
      selectedQuestion.question === question
    ) {
      setSelectedQuestion({
        section,
        question,
        questionNumber,
      });
    } else {
      setSelectedQuestion({
        section,
        question,
        questionNumber,
      });
    }
  };

  const getResultAttachments = question => {
    const result = getResult(question);

    if (result.attachmentsMeta && result.attachmentsMeta.length > 0) {
      return result.attachmentsMeta || [];
    }

    return (result.attachments || []).map(x => x.attachmentId);
  };

  const activeReportReviewComments =
    activeReportReviewNote && activeReportReviewNote.reportReviewComments;

  useEffect(() => {
    dispatchSaveStatus({ type: 'RESET' });

    if (activeQuestion) {
      const questionResultId = getResult(activeQuestion).checklistQuestionId;
      getResultHistories({ variables: { entityId: questionResultId, entityClass: 'ChecklistQuestionResult' } });
      setCollapsedBlocks(Object.values(CollapsibleBlock));
      setDisplayFollowUp(false);
    }
  }, [activeQuestion]);

  const renderSingleAlert = () => ALERT_CODES
    .includes(activeQuestion.negativeAlert || getFactoryQuestion(activeQuestion, companies).negativeResultAlert) && (
    <s.AlertNotification>
      <s.AlertNotificationIcon name="alert" />
      {t('ALERT_24_REQUIRED')}
    </s.AlertNotification>
  );

  const renderMultipleAlerts = () => activeQuestion?.alerts?.length &&
    getUniqueAlerts(activeQuestion.alerts).some(alert => ALERT_CODES.includes(alert.negativeAlert)) && (
      <s.AlertNotification>
        <s.AlertNotificationBlock>
          <s.AlertNotificationIcon name="alert" />
          {t('ALERT_24_REQUIRED_MULTIPLE')}
        </s.AlertNotificationBlock>
        <s.AlertNotificationBlock>
          <ul>
            {getUniqueAlerts(activeQuestion.alerts).map(alert => ALERT_CODES.includes(alert.negativeAlert) ? (
              <li key={uuidv4()}>
                {alert.resultFamilyOutcome.result.currentVersion.code}
              </li>
            ) : null)}
          </ul>
        </s.AlertNotificationBlock>
      </s.AlertNotification>
    );

  const renderAlerts = () => activeQuestion?.alerts?.length ? renderMultipleAlerts() : renderSingleAlert();

  const setQuestionResultId = resultId => {
    const currentResult = results.find(result => result.checklistQuestionId === activeQuestion.checklistQuestionId);

    if (currentResult.resultId !== resultId) {
      setResult(activeQuestion, 'resultId', resultId);

      const isNegativeOutcomeSelected = (
        activeQuestionOrChecklistNegativeOutcomes?.map(v => v.resultId)
        || []
      ).includes(resultId);

      if (!isNegativeOutcomeSelected) {
        const newNonCompliances = (getResult(activeQuestion).nonCompliances || []).map(nc => ({
          ...nc,
          findingType: 'previous',
        }));
        setResult(activeQuestion, 'nonCompliances', newNonCompliances);
      }
    }
  };

  const customPanel = checklist?.currentVersion?.customPanel;

  const displayCustomPanel = checklist?.currentVersion?.displayCustomPanel;
  const displayCustomPanelAlways = displayCustomPanel === 'always';

  const setCustomPanelResults = value => setResult(
    activeQuestion,
    'customPanelResults',
    value
  );

  const hasPreviousNonCompliance = (getResult(activeQuestion).nonCompliances || []).filter(nc => nc.findingType === 'previous').length > 0;
  useEffect(() => {
    if (hasPreviousNonCompliance) {
      setDisplayFollowUp(true);
      if (collapsedBlocks.indexOf(CollapsibleBlock.Detail) !== -1) {
        toggleBlock(CollapsibleBlock.Detail);
      }
    }
  }, [hasPreviousNonCompliance]);

  const addRemoveFindingOnResultSelection = (isNegativeResult) =>{
    if(isNegativeResult){
      if((getResult(activeQuestion).nonCompliances || []).length === 0){
        addResultNonCompliance(activeQuestion);
      }
    }else if(!isNegativeResult && (getResult(activeQuestion).nonCompliances || []).length > 0){
        let findingIndex = 0;
        getResult(activeQuestion).nonCompliances.forEach(nonCompliance =>{
          if(!nonCompliance.nonComplianceId && !nonCompliance.lawId && !nonCompliance.legalRequirements && !nonCompliance.detailedFinding && !nonCompliance.recommendations){
            removeResultNonCompliance(activeQuestion, findingIndex);
          }else{
            findingIndex +=1;
          }
        })
      }
  }

  useEffect(() => {
    if(selectedQuestion) {
      if(activeReportReviewNote && !activeReportReviewNote.closedAt) {
        setShowResult(false);
        setShowNote(true);
      }
    }
  }, [selectedQuestion]);

  useEffect(() => {
    if (!(!readonly && isJobStatusReviewOrSaq)) {
      return;
    }

    if (isCollapsed) {
      setSelectedQuestion({});
      return;
    }

    const sections = checklist?.currentVersion?.checklistSections || [];
    let foundSectionIndex = -1;
    let foundQuestionIndex = -1;
    let foundQuestionNumber;

    for (let i = 0; i < sections.length; i+=1) {
      const section = sections[i];
      if (section.questions && section.questions.length > 0) {
        const questions = getSectionQuestions(
        section,
        applyQuestionConditions(section),
      );
      const questionNumber = questions.length > 0 && getQuestionNumber(foundSectionIndex,  questions[0]);
      foundQuestionNumber = questionNumber;
        const indexOfQuestion = section.questions.findIndex(ques => ques === questions[0]);
        
        if (indexOfQuestion !== -1) {
          foundSectionIndex = i;
          foundQuestionIndex = indexOfQuestion;
          break;
        }
      }
    }

    if (foundSectionIndex !== -1) {
      setSelectedQuestion({ section: foundSectionIndex, question: foundQuestionIndex, foundQuestionNumber });
      setShowResult(true);
    } else {
      setSelectedQuestion({});
      setShowResult(false);
    }

  }, [readonly, currentFilter, isCollapsed, job.status, JSON.stringify(checklist)])

  const content = (
    <Box
      left={
        <s.Left>
          <s.MenuWrapper>
            <s.MenuLink
              onClick={() => {
                if (isCollapsed) {
                  expandAll();
                } else {
                  collapseAll();
                }
                setIsCollapsed(!isCollapsed);
              }}
            >
              {isCollapsed
                ? t('EXPAND_ALL_SECTIONS')
                : t('COLLAPSE_ALL_SECTIONS')}
            </s.MenuLink>
            <s.MenuLink
              onClick={() => setQuickJumpOpened(!quickJumpOpened)}
              withIcon
            >
              {t('QUICK_JUMP')}
              <s.QuickJumpIcon name="expand" />
              {quickJumpOpened && (
                <PopoverSelect
                  onChange={value => value.ref.current.scrollIntoView()}
                  options={sectionsOptions}
                />
              )}
            </s.MenuLink>
          </s.MenuWrapper>
          <s.MenuWrapper>
            {filterTags.map(tag => (
              <s.GreySwitchButton
                key={tag}
                onClick={() => setCurrentFilter(tag)}
                selected={currentFilter === tag}
                small
              >
                {getButtonTextOfFilterBy(tag, statistic, results, negativeOutcomeResultIds)}
              </s.GreySwitchButton>
            ))}
          </s.MenuWrapper>
          <s.SectionsWrapper ref={sectionsWrapper} readOnly={!!readonly}>
            {flatToTree(
              checklist?.currentVersion?.checklistSections || [],
              null,
              SurveyType.Checklist
            ).map(
              section => (
                <JobChecklistFormSection
                  key={section.originalIndex}
                  checklist={checklist}
                  allNegativeOutcomes={allNegativeOutcomes}
                  allOtherOutcomeResultIds={allOtherOutcomeResultIds}
                  allOtherOutcomes={allOtherOutcomes}
                  allOutcomes={allOutcomes}
                  allPositiveOutcomes={allPositiveOutcomes}
                  applyQuestionConditions={applyQuestionConditions}
                  checklistOutcomes={checklistOutcomes || []}
                  collapsedSections={collapsedSections}
                  getFactoryQuestion={question => getFactoryQuestion(question, companies)}
                  getResult={getResult}
                  getSectionQuestions={getSectionQuestions}
                  negativeOutcomeResultIds={negativeOutcomeResultIds}
                  positiveOutcomeResultIds={positiveOutcomeResultIds}
                  questionsWithMissingResults={questionsWithMissingResults}
                  reportReviewStatistics={reportReviewStatistics}
                  section={section}
                  sectionsOptions={sectionsOptions}
                  selectedQuestion={selectedQuestion}
                  setResult={setResult}
                  setShowHistory={setShowHistory}
                  setShowNote={setShowNote}
                  setShowResult={setShowResult}
                  toggleSection={toggleSection}
                  toggleSelectedQuestion={toggleSelectedQuestion}
                  typeId={checklist.id}
                  wrapperRef={sectionsOptions.find(c => c.value === section.originalIndex).ref}
                />
              ),
            )}
          </s.SectionsWrapper>
        </s.Left>
      }
      right={
        <s.Right isSticky readOnly={readonly}>
          {isFailed && savingBlocks.length > 0 && !retrying && (
            <s.PopupWrapper>
              <JobFormCountDown
                className="link"
                inProgressTitle={'AutoSave in'}
                onClick={() => retryAutoSave(activeQuestion)}
                time={30}
                title={'AutoSaving'}
              />
            </s.PopupWrapper>
          )}
          <Fragment>
            {activeQuestion && (
              <s.AddNoteButtonWithTitleDiv
                noFlex={!!activeReportReviewNote}
                relative
              >
                <s.QuestionTitle>
                  {t('CHECKLIST')} {selectedQuestion.questionNumber}
                </s.QuestionTitle>
                <s.GreySwitchButton
                  onClick={() => {
                    setShowResult(true);
                    setShowNote(false);
                    setShowHistory(false);
                  }}
                  selected={showResult}
                >
                  {t('RESULT')}
                </s.GreySwitchButton>
                {/* **** */}
                {activeReportReviewNote && (
                  <s.GreySwitchButton
                    onClick={() => {
                      setShowResult(false);
                      setShowNote(true);
                      setShowHistory(false);
                    }}
                    selected={showNote}
                  >
                    {t('REVIEW_NOTES')}
                  </s.GreySwitchButton>
                )}
                {((isUnderReviewing && isReportReviewer) || (isReportNotesAvailableToAuditor && isAuditor)) &&
                  !isCreateNote &&
                  !activeReportReviewNote && (
                    <s.AddButtonWrapper
                      inline
                      onClick={() => {
                        setShowHistory(false);
                        addNote();
                      }}
                    >
                      <s.AddButton name="plus" />
                      <s.AddButtonLabel>{t('ADD_NOTE')}</s.AddButtonLabel>
                    </s.AddButtonWrapper>
                  )}
              </s.AddNoteButtonWithTitleDiv>
            )}
            <ReportReviewBlock
              activeReportReviewComments={activeReportReviewComments}
              activeReportReviewNote={activeReportReviewNote}
              isCreateNote={isCreateNote}
              isReportReviewer={isReportReviewer}
              isUnderReviewing={isUnderReviewing}
              jobId={job.id}
              refetchReportReview={refetchReportReview}
              setIsCreateNote={setIsCreateNote}
              setShowNote={setShowNote}
              setShowResult={setShowResult}
              showNote={showNote}
              type="checklist"
              typeId={checklist.id}
              typeQuestionId={activeQuestion && activeQuestion.id}
              typeVersionId={checklist?.currentVersion?.id}
            />
            {
              !isCreateNote && showHistory && (
                <ResultHistoriesBlock
                  resultHistories={resultHistories}
                />
              )
            }
            {activeQuestion && !isCreateNote && showResult && (
              <Fragment>
                <s.QuestionText>
                  {getFactoryQuestion(activeQuestion, companies).text}
                </s.QuestionText>
                <s.QuestionDescription>
                  {getFactoryQuestion(activeQuestion, companies).description}
                </s.QuestionDescription>
                <s.QuestionResultWrapper
                  error={isBlockSaveFailed('resultId')}
                  flash={isBlockSaveSuccess('resultId')}
                >
                  {
                    (activeQuestionOrChecklistPositiveOutcomes || [])
                      .map((outcome, index) => (
                        <s.QuestionResultSelectCompliant
                          key={`question-${activeQuestion.id}-compliant-outcome-${index}-${outcome.resultId}`}
                          onClick={() => {
                            setQuestionResultId(outcome.resultId);
                            if (activeQuestion?.checklistQuestion?.currentVersion?.isWildCardQuestion) {
                              setSelectedQuestion(null);
                            }
                            addRemoveFindingOnResultSelection(false);
                          }}
                          selected={getResult(activeQuestion).resultId === outcome.resultId}
                        >
                          <span>{outcome.result.currentVersion.code}</span>
                        </s.QuestionResultSelectCompliant>
                      ))
                  }
                  {
                    (activeQuestionOrChecklistNegativeOutcomes || [])
                      .map((outcome, index)=>(
                        <s.QuestionResultSelectNonCompliant
                          key={`question-${activeQuestion.id}-non-compliant-outcome-${index}-${outcome.resultId}`}
                          onClick={() => {
                            setQuestionResultId(outcome.resultId);
                            addRemoveFindingOnResultSelection(true);
                            }
                          }
                          selected={getResult(activeQuestion).resultId === outcome.resultId}
                        >
                          <span>{outcome.result.currentVersion.code}</span>
                        </s.QuestionResultSelectNonCompliant>
                      ))
                  }

                  {
                    (activeQuestionOrChecklistOtherOutcomes || [])
                    .map((outcome, index) => {
                      const selected = getResult(activeQuestion).resultId === outcome.resultId;
                      return (
                        <s.QuestionResultSelectNA
                          key={`question-${activeQuestion.id}-other-outcome-${index}-${outcome.resultId}`}
                          onClick={() => {
                            setQuestionResultId(outcome.resultId)
                            addRemoveFindingOnResultSelection(false);
                            }
                          }
                          selected={selected}
                        >
                          <span>{outcome.result.currentVersion.code}</span>
                        </s.QuestionResultSelectNA>
                      );
                    })
                  }
                </s.QuestionResultWrapper>
                {renderAlerts()}
                <s.BlockLabel>
                  {t('FINDINGS')}
                  <s.ToggleBlock
                    collapsed={isCollapsedBlock(CollapsibleBlock.Legal)}
                    name="expand"
                    onClick={() => toggleBlock(CollapsibleBlock.Legal)}
                  />
                </s.BlockLabel>
                <FormRow collapsed={isCollapsedBlock(CollapsibleBlock.Legal)}>
                  {(getResult(activeQuestion).nonCompliances || []).map(
                    (nonCompliance, index) => (
                      <NonComplianceBlock
                        key={index}
                        checklist={checklist}
                        activeQuestion={activeQuestion}
                        countryId={job.countryId}
                        factory={factory}
                        index={index}
                        isBlockSaveFailed={isBlockSaveFailed}
                        isBlockSaveSuccess={isBlockSaveSuccess}
                        isFiltered={isFiltered}
                        isNegativeOutcomeSelected={
                          (activeQuestionOrChecklistNegativeOutcomes?.map(v => v.resultId) || []).includes(getResult(activeQuestion).resultId)
                        }
                        nonCompliance={nonCompliance}
                        nonCompliancesOptions={nonCompliancesOptions}
                        removeResultNonCompliance={removeResultNonCompliance}
                        setIsFiltered={setIsFiltered}
                        setResultNonCompliance={setResultNonCompliance}
                        setResultNonComplianceByObject={setResultNonComplianceByObject}
                      />
                    )
                  )}
                  {getResult(activeQuestion).nonCompliances.length === 0 &&
                    <s.AddButtonWrapper addFinding onClick={() => addResultNonCompliance(activeQuestion)}>
                      <s.AddButton name="plus" />
                      <s.AddButtonLabel>{t('FINDING')}</s.AddButtonLabel>
                    </s.AddButtonWrapper>
                  }

                  <Clearfix />

                  {activeQuestion && customPanel && ((displayCustomPanelAlways) || ((activeQuestionOrChecklistNegativeOutcomes?.map(v => v.resultId) || []).includes(getResult(activeQuestion).resultId) && !displayCustomPanelAlways)) && (
                    <JobCustomPanelBlock
                      customPanel={customPanel}
                      customPanelResults={getResult(activeQuestion).customPanelResults}
                      job={job}
                      setCustomPanelResults={setCustomPanelResults}
                    />
                  )}
                </FormRow>
                <s.BlockLabel u-mt-60>
                  {t('DETAILS')}
                  <s.BlockFollowUp
                      onClick={() => {
                        setDisplayFollowUp(!displayFollowUp);
                        toggleBlock(CollapsibleBlock.Detail);
                      }}
                  >
                    {displayFollowUp ? t('HIDE_FOLLOW_UP') : t('SHOW_FOLLOW_UP')}
                  </s.BlockFollowUp>
                </s.BlockLabel>
                <FormRow collapsed={isCollapsedBlock(CollapsibleBlock.Detail)}>
                  {displayFollowUp ? (
                    <Fragment>
                      <DatePickerField
                        dateOnly
                        error={isBlockSaveFailed('correctedDate') && 'Fail to save CORRECTED DATE'}
                        flash={isBlockSaveSuccess('correctedDate')}
                        label={t('FORM_CORRECTED_DATE')}
                        onChange={value => setResult(activeQuestion, 'correctedDate', value)}
                        value={getResult(activeQuestion).correctedDate || ''}
                      />
                      <TextField
                        error={isBlockSaveFailed('correctiveActionTaken') && 'Fail to save CORRECTIVE ACTION TAKEN'}
                        flash={isBlockSaveSuccess('correctiveActionTaken')}
                        label={t('FORM_CORRECTIVE_ACTION_TAKEN')}
                        multiline
                        onChange={e => setResult(
                          activeQuestion,
                          'correctiveActionTaken',
                          e.target.value,
                        )}
                        value={getResult(activeQuestion).correctiveActionTaken || ''}
                      />
                    </Fragment>
                  ) : null}
                </FormRow>

                <s.BlockLabel>
                  {t('COMMENTS')}
                  <s.ToggleBlock
                    collapsed={isCollapsedBlock(CollapsibleBlock.Comments)}
                    name="expand"
                    onClick={() => toggleBlock(CollapsibleBlock.Comments)}
                  />
                </s.BlockLabel>
                <FormRow collapsed={isCollapsedBlock(CollapsibleBlock.Comments)}>
                  <TextField
                    error={isBlockSaveFailed('comments') && 'Fail to save comments'}
                    flash={isBlockSaveSuccess('comments')}
                    fullsize
                    multiline
                    onChange={e => setResult(activeQuestion, 'comments', e.target.value)}
                    value={getResult(activeQuestion).comments || ''}
                  />
                </FormRow>

                <Fragment>
                  <s.BlockLabel>
                    {t('ATTACHMENTS')}
                    <s.ToggleBlock
                      collapsed={isCollapsedBlock(CollapsibleBlock.Attachments)}
                      name="expand"
                      onClick={() => toggleBlock(CollapsibleBlock.Attachments)}
                    />
                  </s.BlockLabel>
                  <FormRow collapsed={isCollapsedBlock(CollapsibleBlock.Attachments)}>
                    <Attachments
                      key={JSON.stringify(activeQuestion)}
                      attachmentIds={getResultAttachments(activeQuestion)}
                      disabled={readonly}
                      entity="attachments"
                      onChange={(_filename, _entity, id, meta) => {
                        setResult(
                          activeQuestion,
                          'attachments',
                          (getResult(activeQuestion).attachments || []).concat({
                            attachmentId: id,
                          }),
                        );

                        const currentMeta = (
                          getResult(activeQuestion).attachmentsMeta || []
                        ).map(({ __typename, ...rest }) => ({ ...rest }));

                        setResult(
                          activeQuestion,
                          'attachmentsMeta',
                          currentMeta.concat(meta),
                        );
                      }}
                      onDeleteFile={async (filename, attId) => {
                        if (filename !== 'loading.file' && attId) {
                          setResult(
                            activeQuestion,
                            'attachments',
                            (getResult(activeQuestion).attachments || [])
                              .filter(({ attachmentId }) => String(attachmentId) !== String(attId)));

                          setResult(
                            activeQuestion,
                            'attachmentsMeta',
                            (getResult(activeQuestion).attachmentsMeta || [])
                              .filter(({ attId: attachmentId }) => String(attachmentId) !== String(attId)));

                          await deleteAttachment({
                            variables: {
                              id: attId,
                            },
                          });
                        }
                      }}
                      pathPrefix={`job_${job.id}`}
                    />
                  </FormRow>
                </Fragment>
              </Fragment>
            )}
          </Fragment>
        </s.Right>
      }
      rightColProps={{
        isSticky: true,
      }}
      stickyContainer
      withoutBorder
    />
  );

  return loading ? <Loader /> : content;
};

JobChecklistFormComponent.propTypes = JobChecklistFormPropTypes;

export const JobChecklistForm = React.memo(withJobContext(JobChecklistFormComponent));
